// AiChatManager.ts
import AiChatService from "./AiChatService";

export interface ChatMessage {
  id: string;
  timestamp: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: any;
}

export interface ChatSession {
  session_id: string;
  user_id: string;
  patient_hn?: string;
  patient_name?: string;
  created_at: string;
  last_active: string;
  is_active: boolean;
  metadata?: any;
  message_count?: number;
}

export interface PatientContext {
  patient_info?: any;
  encounter_summary?: any[];
}

export default class AiChatManager {
  apiToken?: string | null;
  apiService: AiChatService;
  private currentSessionId: string | null = null;
  private currentPatientContext: PatientContext | null = null;
  private messages: ChatMessage[] = [];
  
  // Event callbacks
  onMessagesUpdated?: (messages: ChatMessage[]) => void;
  onSessionChanged?: (sessionId: string | null) => void;
  onPatientContextChanged?: (context: PatientContext | null) => void;
  onError?: (error: string) => void;

  constructor(apiToken: string | null = null) {
    this.apiToken = apiToken;
    this.apiService = new AiChatService(apiToken);
  }

  // Session management
  createChatSession = async (): Promise<ChatSession> => {
    try {
      const session = await this.apiService.createSession();
      this.currentSessionId = session.session_id;
      this.onSessionChanged?.(this.currentSessionId);
      return session;
    } catch (error) {
      this.onError?.(`Failed to create session: ${error.message}`);
      throw error;
    }
  }

  searchSessionsByHn = async (hn: string, limit = 20): Promise<ChatSession[]> => {
    try {
      const data = await this.apiService.getUserSessions(limit);
      const filteredSessions = data.sessions.filter(session => session.patient_hn === hn);
      return filteredSessions;
    } catch (error) {
      this.onError?.(`Failed to search sessions: ${error.message}`);
      throw error;
    }
  }

  activateSession = async (sessionId: string): Promise<void> => {
    try {
      await this.apiService.activateSession(sessionId);
      this.currentSessionId = sessionId;
      this.onSessionChanged?.(this.currentSessionId);
      
      // Load chat history and patient context
      await this.loadSessionData(sessionId);
    } catch (error) {
      this.onError?.(`Failed to activate session: ${error.message}`);
      throw error;
    }
  }

  private loadSessionData = async (sessionId: string): Promise<void> => {
    try {
      // Load chat history
      const historyData = await this.apiService.getChatHistory(sessionId);
      this.messages = historyData.messages || [];
      this.onMessagesUpdated?.(this.messages);
      
      // Load patient context
      const patientContext = await this.apiService.getCurrentPatientContext(sessionId);
      this.currentPatientContext = patientContext;
      this.onPatientContextChanged?.(this.currentPatientContext);
    } catch (error) {
      console.warn('Failed to load session data:', error);
      // Don't throw here as session activation might still be valid
    }
  }

  deactivateSession = async (sessionId: string): Promise<void> => {
    try {
      await this.apiService.deactivateSession(sessionId);
      if (this.currentSessionId === sessionId) {
        this.currentSessionId = null;
        this.currentPatientContext = null;
        this.messages = [];
        this.onSessionChanged?.(null);
        this.onPatientContextChanged?.(null);
        this.onMessagesUpdated?.([]);
      }
    } catch (error) {
      this.onError?.(`Failed to deactivate session: ${error.message}`);
      throw error;
    }
  }

  // Patient management
  setPatient = async (sessionId: string, hn: string): Promise<PatientContext> => {
    try {
      const patientData = await this.apiService.searchPatient(sessionId, hn);
      this.currentPatientContext = patientData;
      this.onPatientContextChanged?.(this.currentPatientContext);
      
      // Reload messages to include the system message about patient loading
      await this.loadChatHistory(sessionId);
      
      return patientData;
    } catch (error) {
      this.onError?.(`Failed to set patient: ${error.message}`);
      throw error;
    }
  }

  getCurrentPatientContext = async (sessionId: string): Promise<PatientContext | null> => {
    try {
      const context = await this.apiService.getCurrentPatientContext(sessionId);
      this.currentPatientContext = context;
      this.onPatientContextChanged?.(this.currentPatientContext);
      return context;
    } catch (error) {
      this.onError?.(`Failed to get patient context: ${error.message}`);
      throw error;
    }
  }

  sendMessageStream = async (sessionId: string, message: string): Promise<void> => {
    if (!this.currentSessionId || this.currentSessionId !== sessionId) {
      throw new Error('No active session or session mismatch');
    }

    // Add user message locally for immediate feedback
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      type: 'user',
      content: message
    };
    
    this.messages = [...this.messages, userMessage];
    this.onMessagesUpdated?.(this.messages);

    // Create placeholder for assistant response
    const assistantMessageId = (Date.now() + 1).toString();
    let assistantMessage: ChatMessage = {
      id: assistantMessageId,
      timestamp: new Date().toISOString(),
      type: 'assistant',
      content: '',
      metadata: { thinking: '', streaming: true }
    };

    this.messages = [...this.messages, assistantMessage];
    this.onMessagesUpdated?.(this.messages);

    try {
      await this.apiService.sendMessageStream(sessionId, message, (chunk) => {
        this.handleStreamChunk(assistantMessageId, chunk);
      });
    } catch (error) {
      // Handle error by replacing the streaming message with error message
      const errorMessage: ChatMessage = {
        id: assistantMessageId,
        timestamp: new Date().toISOString(),
        type: 'system',
        content: 'Sorry, there was an error processing your message. Please try again.',
        metadata: { error: true }
      };
      
      // Replace the streaming message with error message
      this.messages = this.messages.map(msg => 
        msg.id === assistantMessageId ? errorMessage : msg
      );
      
      this.onMessagesUpdated?.(this.messages);
      this.onError?.(`Failed to send message: ${error.message}`);
    }
  }

  private handleStreamChunk = (messageId: string, chunk: any) => {
    const messageIndex = this.messages.findIndex(msg => msg.id === messageId);
    if (messageIndex === -1) return;

    const currentMessage = { ...this.messages[messageIndex] };

    switch (chunk.type) {
      case 'thinking':
        // Update thinking content
        if (!currentMessage.metadata) currentMessage.metadata = {};
        currentMessage.metadata.thinking = chunk.full_reasoning || '';
        currentMessage.metadata.showThinking = true;
        break;

      case 'answer_start':
        // Start showing answer, hide thinking
        if (!currentMessage.metadata) currentMessage.metadata = {};
        currentMessage.metadata.thinking = chunk.reasoning_complete || '';
        currentMessage.metadata.showThinking = false;
        currentMessage.content = '';
        break;

      case 'answer_chunk':
        // Update answer content
        currentMessage.content = chunk.full_answer || '';
        break;

      case 'complete':
        // Finalize message
        currentMessage.content = chunk.full_answer || '';
        if (!currentMessage.metadata) currentMessage.metadata = {};
        currentMessage.metadata.streaming = false;
        currentMessage.metadata.thinking = chunk.full_reasoning || '';
        break;

      case 'error':
        // Handle error
        currentMessage.type = 'system';
        currentMessage.content = chunk.content || 'An error occurred';
        if (!currentMessage.metadata) currentMessage.metadata = {};
        currentMessage.metadata.error = true;
        currentMessage.metadata.streaming = false;
        break;
    }

    // Update the message in the array
    this.messages = [
      ...this.messages.slice(0, messageIndex),
      currentMessage,
      ...this.messages.slice(messageIndex + 1)
    ];

    this.onMessagesUpdated?.(this.messages);
  }

  // Chat management
  sendMessage = async (sessionId: string, message: string): Promise<ChatMessage> => {
    if (!this.currentSessionId || this.currentSessionId !== sessionId) {
      throw new Error('No active session or session mismatch');
    }

    try {
      // Add user message locally for immediate feedback
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        type: 'user',
        content: message
      };
      
      this.messages = [...this.messages, userMessage];
      this.onMessagesUpdated?.(this.messages);

      // Send to API
      const response = await this.apiService.sendMessage(sessionId, message);
      
      // Add assistant response
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        timestamp: new Date().toISOString(),
        type: 'assistant',
        content: response.message
      };
      
      this.messages = [...this.messages, assistantMessage];
      this.onMessagesUpdated?.(this.messages);
      
      return assistantMessage;
    } catch (error) {
      // Remove the optimistically added user message on error
      this.messages = this.messages.slice(0, -1);
      
      // Add error message
      const errorMessage: ChatMessage = {
        id: (Date.now() + 2).toString(),
        timestamp: new Date().toISOString(),
        type: 'system',
        content: 'Sorry, there was an error sending your message. Please try again.',
        metadata: { error: true }
      };
      
      this.messages = [...this.messages, errorMessage];
      this.onMessagesUpdated?.(this.messages);
      
      this.onError?.(`Failed to send message: ${error.message}`);
      throw error;
    }
  }

  loadChatHistory = async (sessionId: string): Promise<ChatMessage[]> => {
    try {
      const historyData = await this.apiService.getChatHistory(sessionId);
      this.messages = historyData.messages || [];
      this.onMessagesUpdated?.(this.messages);
      return this.messages;
    } catch (error) {
      this.onError?.(`Failed to load chat history: ${error.message}`);
      throw error;
    }
  }

  clearChatHistory = async (sessionId: string): Promise<void> => {
    try {
      await this.apiService.clearHistory(sessionId);
      this.messages = [];
      this.onMessagesUpdated?.([]);
    } catch (error) {
      this.onError?.(`Failed to clear chat history: ${error.message}`);
      throw error;
    }
  }

  summarizePatientStream = async (sessionId: string): Promise<void> => {
    if (!this.currentSessionId || this.currentSessionId !== sessionId) {
      throw new Error('No active session or session mismatch');
    }

    // if (!this.currentPatientContext?.patient_info) {
    //   throw new Error('No patient data available for summary');
    // }

    // Create placeholder for summary message
    const summaryMessageId = Date.now().toString();
    let summaryMessage: ChatMessage = {
      id: summaryMessageId,
      timestamp: new Date().toISOString(),
      type: 'assistant',
      content: '',
      metadata: { 
        summary: true, 
        streaming: true,
        thinking: ''
      }
    };

    this.messages = [...this.messages, summaryMessage];
    this.onMessagesUpdated?.(this.messages);

    try {
      await this.apiService.summarizePatientStream(sessionId, (chunk) => {
        this.handleSummaryStreamChunk(summaryMessageId, chunk);
      });
    } catch (error) {
      // Handle error by replacing the streaming message with error message
      const errorMessage: ChatMessage = {
        id: summaryMessageId,
        timestamp: new Date().toISOString(),
        type: 'system',
        content: 'Sorry, there was an error generating the summary. Please try again.',
        metadata: { error: true }
      };
      
      // Replace the streaming message with error message
      this.messages = this.messages.map(msg => 
        msg.id === summaryMessageId ? errorMessage : msg
      );
      
      this.onMessagesUpdated?.(this.messages);
      this.onError?.(`Failed to generate summary: ${error.message}`);
    }
  }

  private handleSummaryStreamChunk = (messageId: string, chunk: any) => {
    const messageIndex = this.messages.findIndex(msg => msg.id === messageId);
    if (messageIndex === -1) return;

    const currentMessage = { ...this.messages[messageIndex] };

    switch (chunk.type) {
      case 'summary_start':
        // Initial status
        if (!currentMessage.metadata) currentMessage.metadata = {};
        currentMessage.metadata.status = chunk.content;
        break;

      case 'thinking':
        // Update thinking content for summary
        if (!currentMessage.metadata) currentMessage.metadata = {};
        currentMessage.metadata.thinking = chunk.full_reasoning || '';
        currentMessage.metadata.showThinking = true;
        currentMessage.content = 'Analyzing patient data...';
        break;

      case 'answer_start':
        // Start showing summary, hide thinking
        if (!currentMessage.metadata) currentMessage.metadata = {};
        currentMessage.metadata.thinking = chunk.reasoning_complete || '';
        currentMessage.metadata.showThinking = false;
        currentMessage.content = '';
        break;

      case 'answer_chunk':
        // Update summary content
        currentMessage.content = chunk.full_answer || '';
        break;

      case 'complete':
        // Finalize summary message
        currentMessage.content = chunk.full_answer || '';
        if (!currentMessage.metadata) currentMessage.metadata = {};
        currentMessage.metadata.streaming = false;
        currentMessage.metadata.thinking = chunk.full_reasoning || '';
        currentMessage.metadata.summary = true;
        break;

      case 'summary_saved':
        // Summary has been saved to history
        if (!currentMessage.metadata) currentMessage.metadata = {};
        currentMessage.metadata.saved = true;
        break;

      case 'error':
        // Handle error
        currentMessage.type = 'system';
        currentMessage.content = chunk.content || 'An error occurred while generating summary';
        if (!currentMessage.metadata) currentMessage.metadata = {};
        currentMessage.metadata.error = true;
        currentMessage.metadata.streaming = false;
        break;
    }

    // Update the message in the array
    this.messages = [
      ...this.messages.slice(0, messageIndex),
      currentMessage,
      ...this.messages.slice(messageIndex + 1)
    ];

    this.onMessagesUpdated?.(this.messages);
  }

  summarizePatient = async (sessionId: string): Promise<ChatMessage> => {
    try {
      const summary = await this.apiService.summarizePatient(sessionId);
      
      // Add summary as a message
      const summaryMessage: ChatMessage = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        type: 'assistant',
        content: summary.message,
        metadata: { summary: true }
      };
      
      this.messages = [...this.messages, summaryMessage];
      this.onMessagesUpdated?.(this.messages);
      
      return summaryMessage;
    } catch (error) {
      this.onError?.(`Failed to summarize patient: ${error.message}`);
      throw error;
    }
  }

  // Getters
  getCurrentSessionId = (): string | null => this.currentSessionId;
  getCurrentPatientContextData = (): PatientContext | null => this.currentPatientContext;
  getMessages = (): ChatMessage[] => this.messages;
}